import { GuildOffer } from '@shared/types/offers';
import clsx from 'clsx';
import { useTranslation } from 'i18n/useTranslation';
import React, { useCallback, useEffect, useMemo } from 'react';
import { buttons } from '../../constants/assets';

export interface GuildMember {
  pixionId: string;
  name: string;
}

interface GuildMemberSelectionProps {
  members: GuildMember[];
  offer: GuildOffer;
  selectedMembers: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  className?: string;
  selectedQuantity?: number;
}

export const GuildMemberSelection: React.FC<GuildMemberSelectionProps> = ({
  offer,
  members,
  selectedMembers,
  onSelectionChange,
  className,
  selectedQuantity,
}) => {
  const { guildMemberSelectionTranslations } = useTranslation();

  const isUnlimited = useCallback((value: number | null | undefined): boolean => (
    value === null || value === undefined || value < 0
  ), []);

  const getRemainingSupply = useCallback((memberId: string): number | null => {
    if (Object.keys(offer.guildMemberSupplies).length === 0) {
      return isUnlimited(offer.maxMemberSupply) ? null : offer.maxMemberSupply;
    }

    if (memberId in offer.guildMemberSupplies) {
      const memberSupply = offer.guildMemberSupplies[memberId];
      if (isUnlimited(offer.maxMemberSupply)) {
        return null;
      }
      return memberSupply;
    }

    return isUnlimited(offer.maxMemberSupply) ? null : offer.maxMemberSupply;
  }, [offer.guildMemberSupplies, offer.maxMemberSupply, isUnlimited]);

  const availableMembers = useMemo(
    () => members.filter((member) => {
      const remainingSupply = getRemainingSupply(member.pixionId);
      // Member is available if supply is unlimited (null) or > 0
      return remainingSupply === null || remainingSupply > 0;
    }),
    [members, getRemainingSupply],
  );

  const isAllSelected = useMemo(
    () => availableMembers.length > 0
      && availableMembers.every((member) => selectedMembers.includes(member.pixionId)),
    [availableMembers, selectedMembers],
  );

  const exceedsLimit = useMemo(() => selectedMembers.some((memberId) => {
    const remainingSupply = getRemainingSupply(memberId);
    if (remainingSupply === null) {
      return false;
    }
    return (selectedQuantity || 1) > remainingSupply;
  }), [selectedMembers, selectedQuantity, getRemainingSupply]);

  const membersExceedingLimit = useMemo(() => selectedMembers.filter((memberId) => {
    const remainingSupply = getRemainingSupply(memberId);
    if (remainingSupply === null) {
      return false;
    }
    return (selectedQuantity || 1) > remainingSupply;
  }), [selectedMembers, selectedQuantity, getRemainingSupply]);

  useEffect(() => {
    if (exceedsLimit && membersExceedingLimit.length > 0) {
      const validMembers = selectedMembers.filter((memberId) => {
        const remainingSupply = getRemainingSupply(memberId);
        if (remainingSupply === null) {
          return true;
        }
        return (selectedQuantity || 1) <= remainingSupply;
      });
      onSelectionChange(validMembers);
    }
  }, [
    selectedQuantity,
    exceedsLimit,
    membersExceedingLimit,
    selectedMembers,
    onSelectionChange,
    getRemainingSupply,
  ]);

  const handleSelectAll = () => {
    if (isAllSelected) {
      onSelectionChange([]);
    } else {
      const allAvailableIds = availableMembers.map((member) => member.pixionId);
      onSelectionChange(allAvailableIds);
    }
  };

  const handleMemberToggle = (memberId: string) => {
    const remainingSupply = getRemainingSupply(memberId);

    if (remainingSupply !== null && (selectedQuantity || 1) > remainingSupply) {
      return;
    }

    if (selectedMembers.includes(memberId)) {
      onSelectionChange(selectedMembers.filter((id) => id !== memberId));
    } else {
      onSelectionChange([...selectedMembers, memberId]);
    }
  };

  const isMemberDisabled = (member: GuildMember) => {
    const remainingSupply = getRemainingSupply(member.pixionId);
    if (remainingSupply === null) {
      return false;
    }
    return remainingSupply === 0 || (selectedQuantity || 1) > remainingSupply;
  };

  const getRewardStatusText = (member: GuildMember) => {
    const remainingSupply = getRemainingSupply(member.pixionId);
    const maxSupply = offer.maxMemberSupply;

    if (remainingSupply === null || isUnlimited(maxSupply)) {
      if (Object.keys(offer.guildMemberSupplies).length === 0) {
        return guildMemberSelectionTranslations.rewardsAvailable;
      }
      return null;
    }

    if (remainingSupply === 0) {
      return `0/${maxSupply} ${guildMemberSelectionTranslations.rewardsPerPixionId}`;
    }

    if ((selectedQuantity || 1) > remainingSupply) {
      return `${remainingSupply}/${maxSupply} ${guildMemberSelectionTranslations.rewardsPerPixionId} (${guildMemberSelectionTranslations.overTheLimit})`;
    }

    return `${remainingSupply}/${maxSupply} ${guildMemberSelectionTranslations.rewardsAvailable}`;
  };

  return (
    <div className={clsx('w-full', className)}>
      <div className="mb-3 flex items-center justify-between sm:mx-2 lg:mx-0 xl:mx-2 1xl:mx-0">
        <h2 className="font-border poppins-lg text-white">
          {guildMemberSelectionTranslations.selectGuildMembers}
        </h2>
        <div className="flex items-center gap-2">
          <span className="font-border poppins-lg text-white">{guildMemberSelectionTranslations.all}</span>
          <button
            type="button"
            onClick={handleSelectAll}
            className="flex size-6 items-center justify-center bg-toggle-blue bg-cover bg-center bg-no-repeat transition-colors"
          >
            {isAllSelected && (
              <img src={buttons.checkIcon} alt="check" />
            )}
          </button>
        </div>
      </div>

      <div className="h-[253px] space-y-2 overflow-y-auto rounded-md bg-[#0E122C] p-3">
        {members.map((member) => {
          const isDisabled = isMemberDisabled(member);
          const remainingSupply = getRemainingSupply(member.pixionId);
          return (
            <div
              key={member.pixionId}
              className={clsx(
                'flex items-center justify-between rounded-lg p-3 transition-colors',
                (() => {
                  if (isDisabled) {
                    return (remainingSupply === 0
                      ? 'cursor-not-allowed bg-[#595959]'
                      : 'cursor-not-allowed bg-[#E44343]');
                  }
                  return 'cursor-pointer bg-[#3A4982]';
                })(),
              )}
              onClick={() => !isDisabled && handleMemberToggle(member.pixionId)}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && !isDisabled) {
                  e.preventDefault();
                  handleMemberToggle(member.pixionId);
                }
              }}
              role="button"
              tabIndex={isDisabled ? -1 : 0}
              aria-label={`Select ${member.name} - ${getRewardStatusText(member)}`}
            >
              <div className="flex flex-col">
                <span className="font-border font-poppins text-[12.5px] font-black tracking-wide text-white">
                  {member.name}
                </span>
                <span className={clsx(
                  'font-poppins text-[10px] font-medium',
                  isDisabled ? 'text-white' : 'text-lightBlue',
                )}
                >
                  {getRewardStatusText(member)}
                </span>
              </div>
              <button
                type="button"
                className={clsx(
                  'flex size-6 items-center justify-center bg-cover bg-center bg-no-repeat',
                  (() => {
                    if (isDisabled) {
                      return (remainingSupply === 0
                        ? 'cursor-not-allowed bg-toggle-gray'
                        : 'cursor-not-allowed bg-toggle-red');
                    }
                    return 'cursor-pointer bg-toggle-blue';
                  })(),
                )}
                disabled={isDisabled}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isDisabled) {
                    handleMemberToggle(member.pixionId);
                  }
                }}
              >
                {selectedMembers.includes(member.pixionId) && !isDisabled && (
                  <img src={buttons.checkIcon} alt="check" />
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
